import { POSTFLOP_TRAINING_SPOTS } from '../data/postflopSpots'
import { applyPreset, createDefaultConfig } from '../data/presets'
import { RANKS_ASC, SUITS } from './cards'
import type { Card, SolverConfig, SolverPresetId } from '../types/solver'
import { applyActionSet, type BatchActionSet } from './tree'

export type BatchStreetMix = 'river' | 'turn-river' | 'mixed'

export interface BatchPlanOptions {
  count: number
  streetMix: BatchStreetMix
  maxIterations: number
  memoryLimitMb: number
  actionSet?: BatchActionSet
}

const TEXTURE_FLOPS: Array<[Card, Card, Card]> = [
  ['As', '7d', '2c'],
  ['Ks', 'Qh', '4s'],
  ['9s', '8s', '6d'],
  ['Qc', 'Qd', '5s'],
  ['7h', '6h', '5c'],
  ['Ac', 'Kc', 'Qc'],
  ['Jd', 'Ts', '4d'],
  ['4s', '4c', '2h'],
  ['Ah', '9h', '3h'],
  ['Tc', '8d', '2s'],
  ['Kd', '7d', '6c'],
  ['8c', '5d', '3s'],
  ['Jh', 'Jc', '8s'],
  ['Ad', 'Td', '5c'],
  ['6s', '3s', '2d'],
  ['Kh', '9c', '9d'],
]

function shuffle<T>(items: T[], random: () => number): T[] {
  const next = [...items]
  for (let index = next.length - 1; index > 0; index -= 1) {
    const target = Math.floor(random() * (index + 1))
    ;[next[index], next[target]] = [next[target], next[index]]
  }
  return next
}

function deckWithout(cards: Card[]): Card[] {
  const dead = new Set(cards)
  return RANKS_ASC.split('').flatMap((rank) => SUITS.split('').map((suit) => `${rank}${suit}`)).filter((card) => !dead.has(card))
}

function presetFor(index: number, mix: BatchStreetMix): SolverPresetId {
  if (mix === 'river') return 'river-test'
  if (mix === 'turn-river') return index % 2 === 0 ? 'river-test' : 'turn-test'
  const sequence: SolverPresetId[] = ['river-test', 'turn-test', 'river-test', 'flop-lite', 'turn-test']
  return sequence[index % sequence.length]
}

export function createBatchPlan(
  options: BatchPlanOptions,
  random: () => number = Math.random,
): SolverConfig[] {
  const count = Math.max(1, Math.min(20, Math.floor(options.count)))
  const batchId = crypto.randomUUID()
  const flops = shuffle(TEXTURE_FLOPS, random)
  const spots = shuffle(POSTFLOP_TRAINING_SPOTS, random)

  return Array.from({ length: count }, (_, index) => {
    const preset = presetFor(index, options.streetMix)
    const config = applyActionSet(applyPreset(createDefaultConfig(preset), preset), options.actionSet ?? 'strategic')
    const spot = spots[index % spots.length]
    const flop = flops[index % flops.length]
    const runout = shuffle(deckWithout(flop), random)

    config.board = { flop: [...flop] }
    if (preset === 'turn-test' || preset === 'river-test') config.board.turn = runout[0]
    if (preset === 'river-test') config.board.river = runout[1]
    config.positions = { ...spot.positions }
    config.potBb = spot.potBb
    config.effectiveStackBb = spot.effectiveStackBb
    config.ranges = structuredClone(spot.ranges)
    config.accuracy.maxIterations = options.maxIterations
    config.memoryLimitMb = options.memoryLimitMb
    config.trainingContext = {
      batchId,
      spotId: spot.id,
      spotLabel: spot.label,
      generated: true,
    }
    return config
  })
}

export function batchMixLabel(mix: BatchStreetMix): string {
  if (mix === 'river') return 'Riverのみ（最速）'
  if (mix === 'turn-river') return 'Turn / River'
  return 'Flop / Turn / River'
}
